import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, MapPin, Loader2, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AddressConfirmationCard — mounted on the Wallet page. Blocks credit
 * top-up until the buyer supplies a full billing address. Runs the
 * fraud-verification quorum server-side on submit (IP + billing address
 * cross-check) so we can flag obvious spoofers before they charge tax.
 *
 * Props:
 *   token      — auth session token
 *   onConfirmed(address) — called after a successful confirmation
 */
const AddressConfirmationCard = ({ token, onConfirmed }) => {
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [address, setAddress] = useState({
    address_line1: "", address_line2: "", city: "", state: "",
    postal_code: "", country: "US",
  });
  const [busy, setBusy] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [ipHint, setIpHint] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/buyer/address`, { headers: { Authorization: `Bearer ${token}` } });
      setConfirmed(!!data.confirmed);
      if (data.address && Object.keys(data.address).length) {
        setAddress({
          address_line1: data.address.address_line1 || "",
          address_line2: data.address.address_line2 || "",
          city: data.address.city || "",
          state: data.address.state || "",
          postal_code: data.address.postal_code || "",
          country: data.address.country || "US",
        });
      }
      setVerdict(data.quorum_last);
    } catch (e) {
      // silent — user may not be logged in yet
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Auto-detect IP country hint via public tax quote endpoint (returns
  // quorum → signals.ip.country). Purely a UX prefill, not a security
  // signal — the server does the real cross-check on submit.
  useEffect(() => {
    if (confirmed) return;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/tax/quote`, { subtotal_cents: 100 });
        const c = data?.quorum?.signals?.ip?.country;
        if (c) {
          setIpHint(c);
          setAddress((a) => a.country || a.country === "US" ? { ...a, country: a.country || c } : a);
        }
      } catch (_e) { /* offline / rate limited — no big deal */ }
    })();
  }, [confirmed]);

  const submit = async (e) => {
    e.preventDefault();
    if (!address.address_line1 || !address.city || !address.postal_code || !address.country) {
      return toast.error("Full address is required by law for tax purposes.");
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/buyer/address/confirm`, address, { headers: { Authorization: `Bearer ${token}` } });
      setVerdict(data.quorum);
      setConfirmed(true);
      toast.success(data.flagged
        ? "Address confirmed — flagged for admin review (soft flag)"
        : "Address confirmed — you can now top up your wallet");
      if (onConfirmed) onConfirmed(data.address);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Address confirmation failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  if (confirmed) {
    return (
      <section className="rounded-xl border border-[#8DB87A]/30 bg-[#8DB87A]/[0.06] p-4 flex items-start gap-3" data-testid="address-confirmed-card">
        <ShieldCheck className="w-5 h-5 text-[#8DB87A] mt-0.5" />
        <div className="flex-1">
          <p className="font-bold text-white text-sm">Billing address confirmed</p>
          <p className="text-xs text-[#8A92A0]">{address.address_line1}, {address.city}, {address.state} {address.postal_code}, {address.country}</p>
          <button
            onClick={() => setConfirmed(false)}
            className="mt-2 text-[10px] uppercase tracking-widest text-[#8A92A0] hover:text-white"
            data-testid="address-edit-btn"
          >
            Edit address
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-[#EBB84C]/40 bg-gradient-to-br from-[#EBB84C]/[0.07] to-[#0A0E18] p-5" data-testid="address-confirm-card">
      <div className="flex items-start gap-3 mb-4">
        <ShieldAlert className="w-5 h-5 text-[#EBB84C] mt-0.5" />
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#EBB84C] font-bold">Address required</p>
          <h2 className="font-['Unbounded'] text-lg font-bold text-white">Confirm your billing address</h2>
          <p className="text-xs text-[#8A92A0] mt-1">
            Full address is <strong className="text-white">required by law for tax purposes</strong>. We use it to charge the correct sales tax and to protect the platform from fraud. {ipHint ? `We detected your IP in ${ipHint}.` : ""}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3" data-testid="address-form">
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
          <label className="col-span-6">
            <span className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Street address</span>
            <input
              type="text" required value={address.address_line1}
              onChange={(e) => setAddress({ ...address, address_line1: e.target.value })}
              className="w-full mt-1 bg-black/40 border border-[#1A2030] rounded-lg px-3 py-2 text-white text-sm"
              placeholder="123 Main St"
              data-testid="address-line1-input"
            />
          </label>
          <label className="col-span-6 sm:col-span-3">
            <span className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Apt / Suite (opt)</span>
            <input
              type="text" value={address.address_line2}
              onChange={(e) => setAddress({ ...address, address_line2: e.target.value })}
              className="w-full mt-1 bg-black/40 border border-[#1A2030] rounded-lg px-3 py-2 text-white text-sm"
              data-testid="address-line2-input"
            />
          </label>
          <label className="col-span-3 sm:col-span-3">
            <span className="text-[10px] uppercase tracking-widest text-[#8A92A0]">City</span>
            <input
              type="text" required value={address.city}
              onChange={(e) => setAddress({ ...address, city: e.target.value })}
              className="w-full mt-1 bg-black/40 border border-[#1A2030] rounded-lg px-3 py-2 text-white text-sm"
              data-testid="address-city-input"
            />
          </label>
          <label className="col-span-3 sm:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-[#8A92A0]">State / Region</span>
            <input
              type="text" value={address.state}
              onChange={(e) => setAddress({ ...address, state: e.target.value.toUpperCase() })}
              className="w-full mt-1 bg-black/40 border border-[#1A2030] rounded-lg px-3 py-2 text-white text-sm uppercase"
              placeholder="NY"
              data-testid="address-state-input"
            />
          </label>
          <label className="col-span-3 sm:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-[#8A92A0]">ZIP / Postal</span>
            <input
              type="text" required value={address.postal_code}
              onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
              className="w-full mt-1 bg-black/40 border border-[#1A2030] rounded-lg px-3 py-2 text-white text-sm"
              placeholder="10001"
              data-testid="address-postal-input"
            />
          </label>
          <label className="col-span-6 sm:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Country</span>
            <div className="relative mt-1">
              <Globe className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A92A0]" />
              <input
                type="text" required maxLength={2} value={address.country}
                onChange={(e) => setAddress({ ...address, country: e.target.value.toUpperCase() })}
                className="w-full pl-8 pr-3 py-2 bg-black/40 border border-[#1A2030] rounded-lg text-white text-sm uppercase font-mono"
                placeholder="US"
                data-testid="address-country-input"
              />
            </div>
          </label>
        </div>

        <button
          type="submit"
          disabled={busy}
          className="w-full h-11 rounded-full bg-[#EBB84C] text-black font-bold text-sm hover:bg-[#F0C464] disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="address-confirm-submit-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
          {busy ? "Confirming…" : "Confirm & unlock wallet"}
        </button>
      </form>

      {verdict?.verdict === "flag" && (
        <p className="mt-3 text-xs text-[#EBB84C]">
          ⚠️ Soft-flagged: {verdict.note}
        </p>
      )}
    </section>
  );
};

export default AddressConfirmationCard;
